/**
 * Authenticated Layout
 *
 * Wraps protected routes with:
 * - Authentication check with redirect to login
 * - Header with user menu and environment selector
 * - Sidebar navigation with capability-gated admin section
 */

import type { Capability } from "@tatvex/types";
import {
  LogOut,
  Settings,
  User,
  ChevronDown,
  Layers,
  Folder,
  Users,
  FileText,
  Activity,
  Building2,
  Shield,
  ClipboardCheck,
  Package,
  MousePointerClick,
} from "lucide-react";
import type { ReactElement } from "react";
import { useCallback } from "react";
import { Outlet, Navigate, useNavigate, useLocation, Link } from "react-router-dom";

import { ApprovalBadge } from "../components/approval/ApprovalBadge";
import { useAuthStore } from "../store/authStore";
import { ENVIRONMENT_LABELS, type Environment } from "../types";

export function AuthenticatedLayout(): ReactElement {
  const { isAuthenticated, user, environment, tenantSlug, logout, setEnvironment } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login");
  }, [logout, navigate]);

  const handleEnvironmentChange = useCallback(
    (newEnv: Environment) => {
      setEnvironment(newEnv);
    },
    [setEnvironment]
  );

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Header */}
      <header className="flex h-14 items-center justify-between border-b border-border bg-background-secondary px-4">
        {/* Logo and tenant */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Layers className="h-6 w-6 text-primary" />
            <span className="text-lg font-semibold">Tatvex Studio</span>
          </div>
          {tenantSlug && (
            <div className="flex items-center gap-1 rounded-md bg-background px-2 py-1 text-sm">
              <span className="text-foreground-muted">Tenant:</span>
              <span className="font-medium">{tenantSlug}</span>
            </div>
          )}
        </div>

        {/* Environment selector and user menu */}
        <div className="flex items-center gap-4">
          {/* Environment selector */}
          <EnvironmentSelector environment={environment} onChange={handleEnvironmentChange} />

          {/* User menu */}
          <UserMenu user={user} onLogout={handleLogout} />
        </div>
      </header>

      {/* Main content area with sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar />

        {/* Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ============================================================================
// Sidebar Component
// ============================================================================

interface NavItem {
  readonly label: string;
  readonly href: string;
  readonly icon: ReactElement;
  readonly requiredCapability?: Capability;
}

interface NavSection {
  readonly title: string;
  readonly items: readonly NavItem[];
  readonly requiredCapabilities?: readonly Capability[];
}

function Sidebar(): ReactElement {
  const location = useLocation();
  const { hasCapability, hasAnyCapability } = useAuthStore();

  // Extract current vertical from URL for contextual nav items
  const verticalMatch = location.pathname.match(/^\/studio\/([^/]+)/);
  const currentVertical =
    verticalMatch && verticalMatch[1] !== "admin" && verticalMatch[1] !== "approvals"
      ? verticalMatch[1]
      : null;

  const configItems: NavItem[] = [
    {
      label: "Verticals",
      href: "/studio",
      icon: <Folder className="h-4 w-4" />,
    },
  ];

  // Show Providers link when viewing a vertical
  if (currentVertical) {
    configItems.push({
      label: "Providers",
      href: `/studio/${currentVertical}/providers`,
      icon: <Package className="h-4 w-4" />,
      requiredCapability: "products:read",
    });
  }

  configItems.push({
    label: "Approvals",
    href: "/studio/approvals",
    icon: <ClipboardCheck className="h-4 w-4" />,
    requiredCapability: "config:read",
  });

  const navSections: readonly NavSection[] = [
    {
      title: "Configuration",
      items: configItems,
    },
    {
      title: "Admin",
      requiredCapabilities: ["admin:tenants", "admin:users", "admin:audit"],
      items: [
        {
          label: "Tenants",
          href: "/studio/admin/tenants",
          icon: <Building2 className="h-4 w-4" />,
          requiredCapability: "admin:tenants",
        },
        {
          label: "Users",
          href: "/studio/admin/users",
          icon: <Users className="h-4 w-4" />,
          requiredCapability: "admin:users",
        },
        {
          label: "Audit Logs",
          href: "/studio/admin/audit",
          icon: <FileText className="h-4 w-4" />,
          requiredCapability: "admin:audit",
        },
        {
          label: "Click Analytics",
          href: "/studio/admin/analytics",
          icon: <MousePointerClick className="h-4 w-4" />,
          requiredCapability: "admin:audit",
        },
        {
          label: "System Health",
          href: "/studio/admin/health",
          icon: <Activity className="h-4 w-4" />,
          requiredCapability: "admin:tenants",
        },
      ],
    },
    {
      title: "Account",
      items: [
        {
          label: "Settings",
          href: "/studio/account/settings",
          icon: <Settings className="h-4 w-4" />,
        },
      ],
    },
  ];

  const isActiveRoute = (href: string): boolean => {
    if (href === "/studio") {
      return (
        location.pathname === "/studio" ||
        (location.pathname.startsWith("/studio/") &&
          !location.pathname.startsWith("/studio/admin") &&
          !location.pathname.startsWith("/studio/approvals") &&
          !location.pathname.startsWith("/studio/account") &&
          !location.pathname.includes("/providers"))
      );
    }
    return location.pathname.startsWith(href);
  };

  return (
    <aside className="flex w-56 flex-col border-r border-border bg-background-secondary">
      <nav className="flex-1 space-y-6 p-4">
        {navSections.map((section) => {
          // Check if user has access to any item in the section
          const hasAccess = section.requiredCapabilities
            ? hasAnyCapability(section.requiredCapabilities)
            : true;

          if (!hasAccess) {
            return null;
          }

          const visibleItems = section.items.filter(
            (item) => !item.requiredCapability || hasCapability(item.requiredCapability)
          );

          if (visibleItems.length === 0) {
            return null;
          }

          return (
            <div key={section.title}>
              <h3 className="mb-2 flex items-center gap-2 px-2 text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                {section.title === "Admin" && <Shield className="h-3 w-3" />}
                {section.title}
              </h3>
              <ul className="space-y-1">
                {visibleItems.map((item) => {
                  const isActive = isActiveRoute(item.href);
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
                          isActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-foreground hover:bg-background hover:text-foreground"
                        }`}
                      >
                        {item.icon}
                        <span className="flex-1">{item.label}</span>
                        {item.label === "Approvals" && <ApprovalBadge />}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

interface EnvironmentSelectorProps {
  readonly environment: Environment;
  readonly onChange: (env: Environment) => void;
}

function EnvironmentSelector({ environment, onChange }: EnvironmentSelectorProps): ReactElement {
  return (
    <div className="relative">
      <select
        value={environment}
        onChange={(e) => onChange(e.target.value as Environment)}
        className="appearance-none rounded-md border border-border bg-background px-3 py-1.5 pr-8 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      >
        {(Object.entries(ENVIRONMENT_LABELS) as [Environment, string][]).map(([env, label]) => (
          <option key={env} value={env}>
            {label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
    </div>
  );
}

interface UserMenuProps {
  readonly user: { name: string; email: string; avatarUrl?: string } | undefined;
  readonly onLogout: () => void;
}

function UserMenu({ user, onLogout }: UserMenuProps): ReactElement {
  return (
    <div className="group relative">
      <button className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-background">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <User className="h-4 w-4" />
          )}
        </div>
        <span className="text-sm font-medium">{user?.name ?? "User"}</span>
        <ChevronDown className="h-4 w-4 text-foreground-muted" />
      </button>

      {/* Dropdown menu */}
      <div className="invisible absolute right-0 top-full z-50 mt-1 min-w-48 rounded-md border border-border bg-background-secondary py-1 opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {user && (
          <div className="border-b border-border px-3 py-2">
            <p className="text-sm font-medium">{user.name}</p>
            <p className="text-xs text-foreground-muted">{user.email}</p>
          </div>
        )}
        <Link
          to="/studio/account/settings"
          className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-background"
        >
          <Settings className="h-4 w-4" />
          Settings
        </Link>
        <button
          onClick={onLogout}
          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-error hover:bg-background"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  );
}
